import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lock, Building2, ShieldCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const BG = "https://static.prod-images.emergentagent.com/jobs/5ffc0df2-bf91-480a-848b-2a4f7a06771c/images/57e4e9de63c3e6ab721a749c1909f52a6f26a6493a0179b4f125ab1057a25428.jpeg";

// Anonymous visitors and non-approved accounts never reach the catalogue or a
// price — they see the trade brand page + login/registration only.
export default function SiteGate({ children }) {
  const [state, setState] = useState("loading"); // loading | open | gated
  const { pathname } = useLocation();
  // Certificate verification (/verify) stays open for QR scans; /coin will be
  // re-added here only when its verify alias is built (never a priced route).
  const bypass = ["/admin", "/login", "/auth", "/verify", "/track-order"].some((p) => pathname.startsWith(p));

  useEffect(() => {
    let alive = true;
    if (!localStorage.getItem("aura_token")) { setState("gated"); return; }
    api.get("/b2b/me")
      .then((r) => { if (alive) setState(r.data.approval_status === "approved" ? "open" : "gated"); })
      .catch(() => { if (alive) setState("gated"); });
    return () => { alive = false; };
  }, [pathname]);

  if (bypass) return children;
  if (state === "loading") return <div className="min-h-screen bg-brand" data-testid="site-gate-loading" />;
  if (state === "open") return children;
  return <TradeLanding onAuthed={() => setState("open")} />;
}

function TradeLanding({ onAuthed }) {
  const [tab, setTab] = useState("login");
  return (
    <div className="min-h-screen bg-brand text-ivory relative overflow-hidden" data-testid="b2b-gate">
      <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-br from-brand via-brand/95 to-brand/80" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-10 lg:py-16 grid lg:grid-cols-2 gap-12 items-center min-h-screen">
        <div>
          <p className="font-mono text-xs tracking-[0.35em] text-gold uppercase">Siyaara · Trade</p>
          <h1 className="font-heading text-4xl lg:text-5xl mt-4 leading-tight">The wholesale coin &amp; jewellery desk for the trade.</h1>
          <p className="text-ivory/70 mt-5 max-w-md text-sm leading-relaxed">
            Live metal-rate pricing, transparent craft charges and certified coins — for jewellers, retailers and corporate buyers. Prices are visible to approved GST-registered accounts only.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-ivory/80">
            <li className="flex items-center gap-3"><ShieldCheck size={18} className="text-gold" /> QR-verifiable, assay-certified coins</li>
            <li className="flex items-center gap-3"><Building2 size={18} className="text-gold" /> Advance-metal basis &amp; account credit terms</li>
            <li className="flex items-center gap-3"><Lock size={18} className="text-gold" /> Login-gated trade pricing</li>
          </ul>
        </div>
        <div className="bg-ivory text-ink rounded-2xl p-6 lg:p-8 shadow-2xl">
          <div className="flex gap-1 bg-brand-light rounded-full p-1 mb-6">
            {["login", "register"].map((t) => (
              <button key={t} onClick={() => setTab(t)}
                className={`flex-1 h-9 rounded-full text-sm font-medium transition-colors ${tab === t ? "bg-brand text-ivory" : "text-ink-2"}`}
                data-testid={`b2b-tab-${t}`}>
                {t === "login" ? "Sign in" : "Register"}
              </button>
            ))}
          </div>
          {tab === "login" ? <LoginForm onAuthed={onAuthed} /> : <RegisterForm goLogin={() => setTab("login")} />}
        </div>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-2">{label}</span>
      <input {...props} className="mt-1 w-full h-10 px-3 rounded-lg border border-brand/15 bg-white text-sm focus:outline-none focus:border-gold" />
    </label>
  );
}

function LoginForm({ onAuthed }) {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await api.post("/b2b/login", { email, password });
      localStorage.setItem("aura_token", r.data.token);
      onAuthed();
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="b2b-login-form">
      <Field label="Work email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="b2b-login-email" />
      <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="b2b-login-password" />
      {err && <p className="text-sm text-red-600" data-testid="b2b-login-error">{err}</p>}
      <button disabled={busy} className="w-full h-11 rounded-full bg-brand text-ivory font-medium disabled:opacity-50" data-testid="b2b-login-submit">
        {busy ? "Signing in…" : "Sign in to trade desk"}
      </button>
    </form>
  );
}

function RegisterForm({ goLogin }) {
  const [f, setF] = useState({ firm_name: "", gstin: "", pan: "", business_address: "", contact_person: "", phone: "", email: "", password: "", nature_of_business: "" });
  const [err, setErr] = useState(""); const [done, setDone] = useState(false); const [busy, setBusy] = useState(false);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.post("/b2b/register", f); setDone(true); }
    catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="text-center py-6" data-testid="b2b-register-done">
      <ShieldCheck className="mx-auto text-gold" size={40} />
      <p className="font-heading text-xl mt-4">Registration received</p>
      <p className="text-sm text-ink-2 mt-2">Our trade desk will review your GST details and approve your account. You'll be able to sign in and see pricing once approved.</p>
      <button onClick={goLogin} className="mt-5 text-sm text-brand underline" data-testid="b2b-register-tologin">Back to sign in</button>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="b2b-register-form">
      <Field label="Firm name" value={f.firm_name} onChange={on("firm_name")} required data-testid="b2b-reg-firm" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="GSTIN" value={f.gstin} onChange={on("gstin")} required data-testid="b2b-reg-gstin" />
        <Field label="PAN (optional)" value={f.pan} onChange={on("pan")} data-testid="b2b-reg-pan" />
      </div>
      <Field label="Business address" value={f.business_address} onChange={on("business_address")} required data-testid="b2b-reg-address" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Contact person" value={f.contact_person} onChange={on("contact_person")} required data-testid="b2b-reg-contact" />
        <Field label="Phone" value={f.phone} onChange={on("phone")} required data-testid="b2b-reg-phone" />
      </div>
      <Field label="Work email" type="email" value={f.email} onChange={on("email")} required data-testid="b2b-reg-email" />
      <Field label="Password" type="password" value={f.password} onChange={on("password")} required data-testid="b2b-reg-password" />
      <Field label="Nature of business" value={f.nature_of_business} onChange={on("nature_of_business")} placeholder="Retailer, wholesaler, corporate gifting…" data-testid="b2b-reg-nature" />
      {err && <p className="text-sm text-red-600" data-testid="b2b-register-error">{err}</p>}
      <button disabled={busy} className="w-full h-11 rounded-full bg-brand text-ivory font-medium disabled:opacity-50" data-testid="b2b-register-submit">
        {busy ? "Submitting…" : "Register trade account"}
      </button>
    </form>
  );
}
